import type { ThinkingEvent } from '@/lib/thinking'
import type { EmberService, StatusSnapshot } from '@/lib/status'
import type { PulseService } from '@/lib/pulse'

/**
 * Telemetry core (R4) — pure reducers over the consolidated live feeds.
 * Transport (EventSources + polls) lives in store.ts; this file is
 * framework-free and fully unit-tested. Flow maps (U5) and the HUD both
 * read this state.
 *
 * Staleness: one consistent meaning across telemetry consumers — the
 * status-poll cadence. Flow maps use this for in-flight node timeout.
 */
export const STALENESS_MS = 30_000

/** How long after the last thinking event the agent counts as active. */
export const AGENT_ACTIVE_WINDOW_MS = 8_000

/** Sessions with bus activity inside this window count as active. */
export const SESSION_WINDOW_MS = 10 * 60_000

/** Ring cap for the voice-event buffer (TovOS toasts read the tail). */
export const VOICE_RING_CAP = 12

/**
 * A voice moment from archie (or the client sim) — the SSE contract from
 * the U1 voice bridge (`event: tovos.voice`). Kept identical between the
 * live feed and `voiceSim.ts` so the surface never branches on source.
 *
 * SECURITY (gated/minimized SSE): the LIVE wire event no longer carries the
 * raw transcript — `text` is ABSENT for real events. The server sends a safe
 * derived `label` instead (heard+target → the target like "/mail"; heard →
 * "command"; reply → "spoke"; wake/sleep → the kind), plus optional
 * `intent`/`target`. Consumers MUST work from `label`/`target`/`intent` and
 * treat `text` as optional. The client sim still synthesizes its own `text`
 * (visual-only) but never drives navigation (sim events are tagged
 * `source: 'sim'` and the nav executor skips them).
 */
export interface VoiceEvent {
  kind: 'wake' | 'sleep' | 'heard' | 'reply'
  /** Raw transcript — ONLY ever present on client-sim events; absent live. */
  text?: string
  intent?: string
  target?: string
  /** Safe, server-derived summary for display (live events always carry it). */
  label?: string
  source: string
  /** ISO-or-ms timestamp from the emitter; also the toast dedupe key. */
  at: string | number
}

export interface ServiceHealth {
  up: number
  degraded: number
  down: number
  total: number
  overall: string
}

export type FeedState = 'connecting' | 'live' | 'lost'

export interface TelemetryState {
  feed: FeedState
  /** null until the first authoritative `counts` replay (HUD shows "--"). */
  mailCounts: Record<string, number> | null
  mailUnread: number | null
  inboxUnread: number | null
  /** Open phase:start runs without a matching done. */
  agentRunning: number
  runs: Record<string, number>
  lastThinking: ThinkingEvent | null
  lastThinkingAt: number | null
  /** session_id → last-seen ms (pruned by SESSION_WINDOW_MS). */
  sessions: Record<string, number>
  /** null until the thinking feed says hello (then 0 is authoritative). */
  activeSessions: number | null
  health: ServiceHealth | null
  services: EmberService[]
  /** Push-heartbeat fleet (source-tagged 'pulse'); composed with the
   *  Emberlight list CLIENT-side only — never merged server-side (R24). */
  pulseServices: PulseService[]
  todosOpen: number | null
  /** Seq-numbered ring of recent thinking events (cap EVENT_RING_CAP) —
   *  consumers that must see EVERY transition (flow maps) read by seq;
   *  lastThinking alone can skip events across batched renders. */
  events: Array<{ seq: number; ev: ThinkingEvent; at: number }>
  nextSeq: number
  /** Recent voice moments (ring-capped at VOICE_RING_CAP) — TovOS toasts. */
  voiceEvents: VoiceEvent[]
  /** archie presence from wake/sleep events; null until first wake/sleep. */
  voicePresence: 'awake' | 'asleep' | null
  /**
   * Voice bridge link state for the FUI presence indicator (clarity — the
   * operator must see that voice comes from archie-on-Tovarchy, not the
   * browser mic). Driven by store.ts as the gated socket opens/connects:
   *   'locked'     — no read key in localStorage → socket NOT opened.
   *   'connecting' — keyed, socket opened, no `hello`/event yet.
   *   'online'     — keyed + the bridge said hello / sent an event.
   * The map view further refines 'online' into archie-online vs standby
   * from `voicePresence` + recent-event freshness.
   */
  voiceLink: 'locked' | 'connecting' | 'online'
}

export const EVENT_RING_CAP = 40

export function initialTelemetryState(): TelemetryState {
  return {
    feed: 'connecting',
    mailCounts: null,
    mailUnread: null,
    inboxUnread: null,
    agentRunning: 0,
    runs: {},
    lastThinking: null,
    lastThinkingAt: null,
    sessions: {},
    activeSessions: null,
    health: null,
    services: [],
    pulseServices: [],
    todosOpen: null,
    events: [],
    nextSeq: 1,
    voiceEvents: [],
    voicePresence: null,
    voiceLink: 'locked',
  }
}

function sum(counts: Record<string, number>): number {
  return Object.values(counts).reduce((s, n) => s + n, 0)
}

/** Authoritative replay: REPLACES state — reconnects never double-count. */
export function applyMailCounts(s: TelemetryState, counts: Record<string, number>): TelemetryState {
  return { ...s, mailCounts: { ...counts }, mailUnread: sum(counts) }
}

/** Optimistic bump for a mail:new push; the next counts replay reconciles. */
export function applyMailNew(s: TelemetryState, e: { account: string; count: number }): TelemetryState {
  const base = s.mailCounts ?? {}
  const counts = { ...base, [e.account]: (base[e.account] ?? 0) + Math.max(1, e.count) }
  return { ...s, mailCounts: counts, mailUnread: sum(counts) }
}

export function applyInboxCounts(s: TelemetryState, unread: number): TelemetryState {
  return { ...s, inboxUnread: unread }
}

export function applyFeedState(s: TelemetryState, feed: FeedState): TelemetryState {
  // The hello after connect makes "0 active sessions" authoritative.
  const activeSessions = feed === 'live' && s.activeSessions === null ? 0 : s.activeSessions
  return { ...s, feed, activeSessions }
}

function runKey(ev: ThinkingEvent): string {
  return `${ev.session_id ?? 'anon'}:${ev.action}`
}

export function applyThinking(s: TelemetryState, ev: ThinkingEvent, now: number): TelemetryState {
  const runs = { ...s.runs }
  if (ev.phase === 'start') runs[runKey(ev)] = now
  else if (ev.phase === 'done') delete runs[runKey(ev)]
  const sessions = ev.session_id ? { ...s.sessions, [ev.session_id]: now } : s.sessions
  const events = [...s.events, { seq: s.nextSeq, ev, at: now }].slice(-EVENT_RING_CAP)
  return {
    ...s,
    runs,
    agentRunning: Object.keys(runs).length,
    lastThinking: ev,
    lastThinkingAt: now,
    sessions,
    activeSessions: countActiveSessions(sessions, now),
    events,
    nextSeq: s.nextSeq + 1,
  }
}

function countActiveSessions(sessions: Record<string, number>, now: number): number {
  return Object.values(sessions).filter((t) => now - t < SESSION_WINDOW_MS).length
}

/** Periodic prune: expires stale sessions and stuck runs (no done event). */
export function pruneTelemetry(s: TelemetryState, now: number): TelemetryState {
  const sessions: Record<string, number> = {}
  for (const [id, t] of Object.entries(s.sessions)) {
    if (now - t < SESSION_WINDOW_MS) sessions[id] = t
  }
  const runs: Record<string, number> = {}
  for (const [k, t] of Object.entries(s.runs)) {
    if (now - t < STALENESS_MS * 10) runs[k] = t // runaway guard: drop runs stuck >5min
  }
  const active = countActiveSessions(sessions, now)
  return {
    ...s,
    sessions,
    runs,
    agentRunning: Object.keys(runs).length,
    activeSessions: s.activeSessions === null ? null : active,
  }
}

export function agentActiveAt(s: TelemetryState, now: number): boolean {
  return s.lastThinkingAt !== null && now - s.lastThinkingAt < AGENT_ACTIVE_WINDOW_MS
}

export function applyStatus(s: TelemetryState, snap: StatusSnapshot): TelemetryState {
  const services = snap.services ?? []
  const summary = snap.summary
  const health: ServiceHealth = summary
    ? {
        up: summary.up,
        degraded: summary.degraded,
        down: summary.down,
        total: summary.total,
        overall: summary.overall,
      }
    : {
        up: services.filter((x) => x.status === 'up').length,
        degraded: services.filter((x) => x.status === 'degraded').length,
        down: services.filter((x) => x.status === 'down').length,
        total: services.length,
        overall: services.some((x) => x.status === 'down')
          ? 'down'
          : services.some((x) => x.status === 'degraded')
            ? 'degraded'
            : 'operational',
      }
  return { ...s, services, health }
}

export function applyTodos(s: TelemetryState, open: number): TelemetryState {
  return { ...s, todosOpen: open }
}

export function applyPulse(s: TelemetryState, services: PulseService[]): TelemetryState {
  return { ...s, pulseServices: services }
}

/**
 * Fold a voice moment into state (R11): append to the ring (cap
 * VOICE_RING_CAP) and let wake/sleep update presence. PURE — both the
 * live SSE (store.ts) and the client sim (voiceSim.ts) call this so the
 * surface never branches on source. `now` is accepted for signature
 * symmetry with the other time-aware reducers (presence derives from the
 * event kind, not the clock).
 *
 * Shape-agnostic by design: it only reads `kind` and stores the event
 * verbatim, so the MINIMIZED live shape (no `text`, with `label`) folds in
 * exactly like the richer sim shape — no branching on source or fields.
 */
export function applyVoice(s: TelemetryState, ev: VoiceEvent, _now: number): TelemetryState {
  const voiceEvents = [...s.voiceEvents, ev].slice(-VOICE_RING_CAP)
  const voicePresence =
    ev.kind === 'wake' ? 'awake' : ev.kind === 'sleep' ? 'asleep' : s.voicePresence
  // A LIVE event proves the keyed bridge is up. Sim events must NOT promote
  // the link (they fire while the bridge is locked/offline) — the store only
  // calls this with the live flag for real SSE, so gate on it there.
  return { ...s, voiceEvents, voicePresence }
}

/**
 * Set the voice-bridge link state for the presence indicator. Pure setter —
 * store.ts drives it: 'locked' when no read key is stored (socket never
 * opened), 'connecting' the moment the keyed socket opens, 'online' on the
 * bridge `hello` or first live event.
 */
export function applyVoiceLink(s: TelemetryState, link: TelemetryState['voiceLink']): TelemetryState {
  if (s.voiceLink === link) return s
  return { ...s, voiceLink: link }
}

/**
 * Client-side Emberlight + pulse composition (AE4). Sources stay
 * distinct in state; this selector unifies the fleet view for the HUD
 * and Status surface. Conflict counts as degraded (a config error needs
 * attention, the service may be fine); archived entries are excluded.
 */
export function composedHealth(s: TelemetryState): ServiceHealth | null {
  const active = s.pulseServices.filter((p) => p.state !== 'archived')
  if (!s.health && active.length === 0) return null
  const base = s.health ?? { up: 0, degraded: 0, down: 0, total: 0, overall: 'operational' }
  const up = base.up + active.filter((p) => p.state === 'up').length
  const degraded =
    base.degraded + active.filter((p) => p.state === 'degraded' || p.state === 'conflict').length
  const down = base.down + active.filter((p) => p.state === 'down').length
  const total = base.total + active.length
  const overall = down > 0 ? 'down' : degraded > 0 ? 'degraded' : 'operational'
  return { up, degraded, down, total, overall }
}
